// client/src/pages/wholesaler/CatalogRequests.js

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Check, X, UserCheck, Users, Clock, ChevronDown, ChevronUp, Package, Search, Copy, ClipboardList, Upload, Download, FileText, Loader } from 'lucide-react';
import Papa from 'papaparse';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import { wholesalerAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// PRODUCT SELECTOR MODAL
// ═══════════════════════════════════════════════
const ProductSelectorModal = ({ business, onClose, onSave, allProducts, existingProductIds, t }) => {
  const [selected, setSelected] = useState(new Set(existingProductIds || []));
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [saving, setSaving] = useState(false);

  const categories = [...new Set(allProducts.map(p => p.category).filter(Boolean))].sort();

  const filtered = allProducts.filter(p => {
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return p.name?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
    }
    return true;
  });

  const toggleProduct = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(filtered.map(p => p._id)));
  const deselectAll = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true);
    await onSave(business.businessId, [...selected]);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              <ClipboardList className="w-5 h-5 inline mr-2 text-blue-600" />
              {t('storefront.manageCatalogFor', 'Manage Catalog for')} {business.businessName}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selected.size} {t('storefront.assignedProducts', 'products assigned')}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b bg-gray-50 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('storefront.searchProducts', 'Search products...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">{t('common:common.all', 'All Categories')}</option>
            {categories.map(c => <option key={c} value={c}>{c?.replace(/_/g, ' ')}</option>)}
          </select>
          <button onClick={selectAll} className="px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">
            {t('storefront.selectAll', 'Select All')}
          </button>
          <button onClick={deselectAll} className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
            {t('storefront.deselectAll', 'Deselect All')}
          </button>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3" />
              <p>{t('common:common.noResults', 'No products found')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map(product => {
                const isSelected = selected.has(product._id);
                return (
                  <label
                    key={product._id}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleProduct(product._id)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.category?.replace(/_/g, ' ')} {product.brand && `• ${product.brand}`}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">€{product.pricing?.unitPrice?.toFixed(2)}/{product.pricing?.unit}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {selected.size} / {allProducts.length} {t('storefront.selected', 'selected')}
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-6 py-2 text-gray-600 border rounded-lg hover:bg-gray-100">
              {t('common:common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? t('common:common.saving', 'Saving...') : t('storefront.saveCatalog', 'Save Catalog')} ({selected.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// COPY CATALOG MODAL
// ═══════════════════════════════════════════════
const CopyCatalogModal = ({ fromBusiness, approvedBusinesses, onClose, onCopy, t }) => {
  const [targetId, setTargetId] = useState('');
  const [copying, setCopying] = useState(false);
  const targets = approvedBusinesses.filter(b => b.businessId !== fromBusiness.businessId);

  const handleCopy = async () => {
    if (!targetId) return;
    setCopying(true);
    await onCopy(fromBusiness.businessId, targetId);
    setCopying(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          <Copy className="w-5 h-5 inline mr-2 text-blue-600" />
          {t('storefront.copyCatalog', 'Copy Catalog')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t('storefront.copyFrom', 'Copy from')}: <strong>{fromBusiness.businessName}</strong>
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('storefront.copyTo', 'Copy to')}:</label>
        <select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="w-full p-3 border rounded-lg mb-4"
        >
          <option value="">{t('storefront.selectBusiness', 'Select a business...')}</option>
          {targets.map(b => (
            <option key={b.businessId} value={b.businessId}>{b.businessName}</option>
          ))}
        </select>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-100">
            {t('common:common.cancel', 'Cancel')}
          </button>
          <button onClick={handleCopy} disabled={!targetId || copying} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {copying ? '...' : t('storefront.copyCatalog', 'Copy Catalog')}
          </button>
        </div>
      </div>
    </div>
  );
};

const CatalogRequests = () => {
  const { t } = useTranslation('wholesaler');
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [privateCatalogMode, setPrivateCatalogMode] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [expandedPending, setExpandedPending] = useState(true);
  const [expandedApproved, setExpandedApproved] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);

  // ── Enterprise Catalog Management State ──
  const [allProducts, setAllProducts] = useState([]);
  const [catalogCounts, setCatalogCounts] = useState({}); // businessId -> product count
  const [selectorBusiness, setSelectorBusiness] = useState(null); // business object for modal
  const [selectorExistingIds, setSelectorExistingIds] = useState([]);
  const [copyFromBusiness, setCopyFromBusiness] = useState(null);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [erpActive, setErpActive] = useState(false);
  const [erpInputs, setErpInputs] = useState({});
  const [erpSaving, setErpSaving] = useState(null);
  const [approvedSearch, setApprovedSearch] = useState('');
  const [erpCustomerSyncing, setErpCustomerSyncing] = useState(false);
  const [erpCustomerSyncResult, setErpCustomerSyncResult] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (location.hash === '#approved') {
      const el = document.getElementById('approved');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, location.hash]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await wholesalerAPI.getCatalogRequests();
      setPrivateCatalogMode(response.data.privateCatalogMode);
      setPendingRequests(response.data.pendingRequests || []);
      setApprovedBusinesses(response.data.approvedBusinesses || []);
      
      // Check if Enterprise tier (for catalog management features)
      try {
        const profileRes = await wholesalerAPI.getProfile();
        const tier = profileRes.data?.wholesaler?.subscriptionTier || profileRes.data?.subscriptionTier;
        if (tier === 'enterprise') {
          setIsEnterprise(true);
          const ws = profileRes.data?.wholesaler || profileRes.data;
          if (ws?.erpIntegration?.isActive) setErpActive(true);
          // Fetch all products for the selector
          const prodRes = await wholesalerAPI.getProducts();
          // Handle both response formats: raw array OR { products: [...] }
          const rawProducts = Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data.products || []);
          setAllProducts(rawProducts.filter(p => p.status === 'active'));
          // Fetch catalog counts for all approved businesses
          try {
            const catalogsRes = await wholesalerAPI.getBusinessCatalogs();
            if (catalogsRes.data?.catalogs) {
              const counts = {};
              catalogsRes.data.catalogs.forEach(c => { counts[c.businessId] = c.productCount; });
              setCatalogCounts(counts);
            }
          } catch { /* no catalogs yet */ }
        }
      } catch { /* profile fetch failed, not critical */ }
    } catch (err) {
      setError(t('catalogRequests.loadFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (businessId) => {
    try {
      setProcessingId(businessId);
      setError('');
      setSuccess('');

      await wholesalerAPI.approveCatalogRequest(businessId);

      setSuccess(t('catalogRequests.approveSuccess'));
      setTimeout(() => setSuccess(''), 3000);

      // Refresh the list
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.message || t('catalogRequests.approveFailed'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (businessId) => {
    setConfirmModal({
      title: t('catalogRequests.rejectTitle', 'Reject Request'),
      message: t('catalogRequests.confirmReject'),
      icon: 'warning',
      confirmLabel: t('catalogRequests.rejectButton', 'Reject'),
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setProcessingId(businessId);
          setError('');
          setSuccess('');
          await wholesalerAPI.rejectCatalogRequest(businessId);
          setSuccess(t('catalogRequests.rejectSuccess'));
          setTimeout(() => setSuccess(''), 3000);
          fetchRequests();
        } catch (err) {
          setError(err.response?.data?.message || t('catalogRequests.rejectFailed'));
        } finally {
          setProcessingId(null);
        }
      }
    });
  };

  const handleRevoke = async (businessId) => {
    setConfirmModal({
      title: t('catalogRequests.revokeTitle', 'Revoke Access'),
      message: t('catalogRequests.confirmRevoke'),
      icon: 'warning',
      confirmLabel: t('catalogRequests.revokeButton', 'Revoke'),
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);

        try {
          setProcessingId(businessId);
          setError('');
          setSuccess('');
          await wholesalerAPI.revokeCatalogAccess(businessId);
          setSuccess(t('catalogRequests.revokeSuccess'));
          setTimeout(() => setSuccess(''), 3000);
          fetchRequests();
        } catch (err) {
          setError(err.response?.data?.message || t('catalogRequests.revokeFailed'));
        } finally {
          setProcessingId(null);
        }
      }
    });
  };

  // ── Enterprise: Open product selector for a business ──
  const handleOpenSelector = async (business) => {
    try {
      // Fetch existing catalog for this business
      const res = await wholesalerAPI.getBusinessCatalog(business.businessId);
      const existingIds = res.data?.catalog?.productIds?.map(id => id.toString()) || [];
      setSelectorExistingIds(existingIds);
      setSelectorBusiness(business);
    } catch {
      setSelectorExistingIds([]);
      setSelectorBusiness(business);
    }
  };

  // ── Enterprise: Save catalog from modal ──
  const handleSaveCatalog = async (businessId, productIds) => {
    try {
      await wholesalerAPI.updateBusinessCatalog(businessId, { products: productIds });
      setCatalogCounts(prev => ({ ...prev, [businessId]: productIds.length }));
      setSelectorBusiness(null);
      setSuccess(t('storefront.catalogSaved', 'Catalog saved!'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save catalog');
    }
  };

  // ── Enterprise: Copy catalog ──
  const handleCopyCatalog = async (fromBusinessId, toBusinessId) => {
    try {
      const res = await wholesalerAPI.copyBusinessCatalog(fromBusinessId, toBusinessId);
      setCatalogCounts(prev => ({ ...prev, [toBusinessId]: res.data?.catalog?.productCount || 0 }));
      setSuccess(t('storefront.catalogCopied', 'Catalog copied!'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy catalog');
    }
  };

  const handleErpCustomerSync = async () => {
    setErpCustomerSyncing(true);
    setErpCustomerSyncResult(null);
    try {
      // Step 1: Sync customers (VAT→TRDR)
      const customerRes = await wholesalerAPI.syncErpCustomers();
      const customerData = customerRes.data;

      // Step 2: Sync discounts (PRCCATEGORY→CustomerDiscount)
      let discountData = null;
      try {
        const discountRes = await wholesalerAPI.syncErpCustomerDiscounts();
        discountData = discountRes.data;
      } catch (discErr) {
        discountData = { error: discErr.response?.data?.message || discErr.message };
      }

      // Step 3: Sync pricing rule configuration (PRCPOLICY→pricingRules)
      let pricingRulesData = null;
      try {
        const rulesRes = await wholesalerAPI.syncErpPricingRules();
        pricingRulesData = rulesRes.data;
      } catch (rulesErr) {
        pricingRulesData = { error: rulesErr.response?.data?.message || rulesErr.message };
      }

      setErpCustomerSyncResult({ customers: customerData, discounts: discountData, pricingRules: pricingRulesData });
      fetchRequests();
    } catch (err) {
      setErpCustomerSyncResult({ error: err.response?.data?.message || err.message });
    } finally {
      setErpCustomerSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-20 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('catalogRequests.title')}
          </h1>
          <p className="text-gray-600">
            {privateCatalogMode 
              ? `🔒 ${t('catalogRequests.privateModeEnabledMessage')}`
              : `🔓 ${t('catalogRequests.privateModeDisabledMessage')}`}
          </p>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Private Mode Disabled Notice */}
        {!privateCatalogMode && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {t('catalogRequests.privateModeDisabledTitle')}
                </h3>
                <p className="text-blue-800 text-sm mb-3">
                  {t('catalogRequests.privateModeDisabledDescription')}
                </p>
                <a 
                  href="/wholesaler/profile" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {t('catalogRequests.goToProfile')}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('catalogRequests.pendingRequests')}</p>
                <p className="text-3xl font-bold text-gray-900">{pendingRequests.length}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('catalogRequests.approvedBusinesses')}</p>
                <p className="text-3xl font-bold text-gray-900">{approvedBusinesses.length}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('catalogRequests.totalPartners')}</p>
                <p className="text-3xl font-bold text-gray-900">{approvedBusinesses.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Pending Requests Section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <button
            onClick={() => setExpandedPending(!expandedPending)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-orange-600" />
              <h2 className="text-xl font-bold text-gray-900">
                {t('catalogRequests.pendingRequests')} ({pendingRequests.length})
              </h2>
            </div>
            {expandedPending ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedPending && (
            <div className="border-t">
              {pendingRequests.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>{t('catalogRequests.noPendingRequests')}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {pendingRequests.map((request) => (
                    <div key={request.businessId} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                            {request.businessName}
                          </h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>📋 {t('catalogRequests.businessType')}: {request.businessType}</p>
                            <p>👤 {t('catalogRequests.contact')}: {request.contactPerson || 'N/A'}</p>
                            <p>📧 {t('catalogRequests.email')}: {request.email}</p>
                            <p>📞 {t('catalogRequests.phone')}: {request.phone}</p>
                            <p className="text-xs text-gray-500">
                              🕐 {t('catalogRequests.requested')}: {new Date(request.requestedAt).toLocaleDateString()} {new Date(request.requestedAt).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            onClick={() => handleApprove(request.businessId)}
                            disabled={processingId === request.businessId}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Check className="w-4 h-4" />
                            {processingId === request.businessId ? t('catalogRequests.processing') : t('catalogRequests.approve')}
                          </button>
                          <button
                            onClick={() => handleReject(request.businessId)}
                            disabled={processingId === request.businessId}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <X className="w-4 h-4" />
                            {t('catalogRequests.reject')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Approved Businesses Section */}
        <div id="approved" className="bg-white rounded-lg shadow-sm scroll-mt-24">
          <button
            onClick={() => setExpandedApproved(!expandedApproved)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">
                {t('catalogRequests.approvedPartners')} ({approvedBusinesses.length})
              </h2>
            </div>
            {expandedApproved ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedApproved && (
            <div className="border-t">
              {approvedBusinesses.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <UserCheck className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>{t('catalogRequests.noApprovedBusinesses')}</p>
                </div>
              ) : (
                <div>
                  {/* Search filter */}
                  {approvedBusinesses.length > 3 && (
                    <div className="px-6 pt-4">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('catalogRequests.searchApproved', 'Search by name, VAT, email...')}
                          value={approvedSearch}
                          onChange={(e) => setApprovedSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                <div className="divide-y">
                  {approvedBusinesses
                    .filter(b => {
                      if (!approvedSearch) return true;
                      const q = approvedSearch.toLowerCase();
                      return (b.businessName || '').toLowerCase().includes(q) ||
                        (b.vatNumber || '').includes(q) ||
                        (b.email || '').toLowerCase().includes(q) ||
                        (b.contactPerson || '').toLowerCase().includes(q) ||
                        (b.erpCustomerId || '').toLowerCase().includes(q);
                    })
                    .map((business) => (
                    <div key={business.businessId} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                              {business.businessName || 'Unknown Business'}
                            </h3>
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              {t('catalogRequests.approvedLabel')}
                            </span>
                            {/* Enterprise: show catalog product count badge */}
                            {isEnterprise && catalogCounts[business.businessId] > 0 && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                {catalogCounts[business.businessId]} {t('storefront.assignedProducts', 'products')}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            {business.vatNumber && (
                              <p className="font-medium text-gray-700">🏛️ {t('catalogRequests.vatNumber', 'VAT')}: {business.vatNumber}</p>
                            )}
                            <p>📋 {t('catalogRequests.businessType')}: {business.businessType || 'N/A'}</p>
                            <p>👤 {t('catalogRequests.contact')}: {business.contactPerson || 'N/A'}</p>
                            <p>📧 {t('catalogRequests.email')}: {business.email || 'N/A'}</p>
                            <p>📞 {t('catalogRequests.phone')}: {business.phone || 'N/A'}</p>
                            <p className="text-xs text-gray-500">
                              🕐 {t('catalogRequests.approved')}: {new Date(business.approvedAt).toLocaleDateString()} {new Date(business.approvedAt).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-shrink-0">
                          {/* Enterprise: Manage Catalog button */}
                          {isEnterprise && (
                            <>
                              <button
                                onClick={() => handleOpenSelector(business)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                              >
                                <ClipboardList className="w-4 h-4" />
                                {t('storefront.manageCatalog', 'Manage Catalog')}
                              </button>
                              {catalogCounts[business.businessId] > 0 && approvedBusinesses.length > 1 && (
                                <button
                                  onClick={() => setCopyFromBusiness(business)}
                                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                                >
                                  <Copy className="w-4 h-4" />
                                  {t('storefront.copyCatalog', 'Copy Catalog')}
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => handleRevoke(business.businessId)}
                            disabled={processingId === business.businessId}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                          >
                            {processingId === business.businessId ? t('catalogRequests.processing') : t('catalogRequests.revokeAccess')}
                          </button>

                          {/* ERP Customer ID (TRDR) */}
                          {erpActive && (
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="text"
                                placeholder={t('erp.erpCustomerId', 'SoftOne TRDR')}
                                value={erpInputs[business.businessId] !== undefined ? erpInputs[business.businessId] : (business.erpCustomerId || '')}
                                onChange={(e) => setErpInputs({ ...erpInputs, [business.businessId]: e.target.value })}
                                className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                disabled={erpSaving === business.businessId}
                                onClick={async () => {
                                  setErpSaving(business.businessId);
                                  try {
                                    await wholesalerAPI.updateBusinessErpCustomer(business.businessId, erpInputs[business.businessId] || business.erpCustomerId || '');
                                    setSuccess(t('erp.saved', 'Saved'));
                                    setTimeout(() => setSuccess(''), 2000);
                                  } catch { setError('Failed to save ERP ID'); }
                                  setErpSaving(null);
                                }}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium hover:bg-purple-200 disabled:opacity-50"
                              >
                                {erpSaving === business.businessId ? '...' : t('common.save')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          )}
        </div>

        {erpActive && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mt-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-start sm:items-center min-w-0">
                <Users className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-800">{t('erp.sync.syncCustomersAndDiscounts', 'Sync Customers & Discounts')}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">{t('erp.sync.syncCustomersAndDiscountsDesc', 'Pull customer data (VAT→TRDR) and discount categories (PRCCATEGORY) from SoftOne in one step')}</p>
                </div>
              </div>
              <button
                onClick={handleErpCustomerSync}
                disabled={erpCustomerSyncing}
                className="w-full sm:w-auto bg-purple-600 text-white px-5 py-2.5 rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm disabled:opacity-50 flex-shrink-0"
              >
                {erpCustomerSyncing ? <><Loader className="w-4 h-4 animate-spin inline mr-2" />{t('erp.sync.syncing', 'Syncing...')}</> : <>{t('erp.sync.syncNow', 'Sync Now')}</>}
              </button>
            </div>

            {erpCustomerSyncResult && (
              <div className={`p-4 rounded-lg ${erpCustomerSyncResult.error ? 'bg-red-50 border border-red-200' : (erpCustomerSyncResult.discounts?.error || erpCustomerSyncResult.pricingRules?.error) ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                {erpCustomerSyncResult.error ? (
                  <p className="text-red-700">{erpCustomerSyncResult.error}</p>
                ) : (
                  <div className="text-green-700 text-sm space-y-2">
                    {/* Customer sync results */}
                    {erpCustomerSyncResult.customers && (
                      <div>
                        <p className="font-semibold">{t('erp.sync.customerSyncComplete', 'Customer sync complete')}</p>
                        <p>{t('erp.sync.total', 'Total in ERP')}: {erpCustomerSyncResult.customers.total} · {t('erp.sync.matched', 'Matched')}: {erpCustomerSyncResult.customers.matched} · {t('erp.sync.customersUpdated', 'Updated')}: {erpCustomerSyncResult.customers.customersUpdated}</p>
                        {erpCustomerSyncResult.customers.usedFallback && <p className="text-yellow-600">{t('erp.sync.usedFallback', 'Used fallback method (slower)')}</p>}
                      </div>
                    )}
                    {/* Discount sync results */}
                    {erpCustomerSyncResult.discounts && !erpCustomerSyncResult.discounts.error && (
                      <div className="border-t border-green-200 pt-2">
                        <p className="font-semibold">{t('erp.sync.discountSyncComplete', 'Discount sync complete')}</p>
                        <p>
                          {t('erp.sync.discountCategories', 'Discount categories')}: {erpCustomerSyncResult.discounts.prccategories} ·
                          {t('erp.sync.totalBusinesses', 'Businesses checked')}: {erpCustomerSyncResult.discounts.totalBusinesses} ·
                          {t('erp.sync.discountsCreated', 'Created')}: {erpCustomerSyncResult.discounts.created} ·
                          {t('erp.sync.discountsUpdated', 'Updated')}: {erpCustomerSyncResult.discounts.updated}
                          {erpCustomerSyncResult.discounts.skipped > 0 && <> · {t('erp.sync.discountsSkipped', 'No discount')}: {erpCustomerSyncResult.discounts.skipped}</>}
                        </p>
                      </div>
                    )}
                    {erpCustomerSyncResult.discounts?.error && (
                      <div className="border-t border-red-200 pt-2">
                        <p className="text-red-700">{t('erp.sync.discountSyncFailed', 'Discount sync failed')}: {erpCustomerSyncResult.discounts.error}</p>
                      </div>
                    )}
                    {erpCustomerSyncResult.pricingRules && !erpCustomerSyncResult.pricingRules.error && (
                      <div className="border-t border-green-200 pt-2">
                        <p className="font-semibold">{t('erp.sync.pricingRulesComplete', 'Pricing rules synced')}</p>
                        <p>
                          {t('erp.sync.rulesFound', 'Rules found')}: {erpCustomerSyncResult.pricingRules.rulesFound || 0} ·
                          {t('erp.sync.activeRules', 'Active')}: {erpCustomerSyncResult.pricingRules.activeRules || 0}
                        </p>
                      </div>
                    )}
                    {erpCustomerSyncResult.pricingRules?.error && (
                      <div className="border-t border-red-200 pt-2">
                        <p className="text-red-700">{t('erp.sync.pricingRulesFailed', 'Pricing rules sync failed')}: {erpCustomerSyncResult.pricingRules.error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ERP Bulk Customer Code Upload — collapsible, at bottom */}
        {erpActive && <ErpBulkUploadSection t={t} />}
      </main>

      {/* Enterprise: Product Selector Modal */}
      {selectorBusiness && (
        <ProductSelectorModal
          business={selectorBusiness}
          onClose={() => setSelectorBusiness(null)}
          onSave={handleSaveCatalog}
          allProducts={allProducts}
          existingProductIds={selectorExistingIds}
          t={t}
        />
      )}

      {/* Enterprise: Copy Catalog Modal */}
      {copyFromBusiness && (
        <CopyCatalogModal
          fromBusiness={copyFromBusiness}
          approvedBusinesses={approvedBusinesses}
          onClose={() => setCopyFromBusiness(null)}
          onCopy={handleCopyCatalog}
          t={t}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />

      <Footer />
    </div>
  );
};

// ============================================
// ERP BULK CUSTOMER CODE UPLOAD
// ============================================
const ErpBulkUploadSection = ({ t }) => {
  const [expanded, setExpanded] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await wholesalerAPI.downloadErpCustomerTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'erp_customer_codes.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template download error:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setResults(null);
    }
  };

  const handleUpload = () => {
    if (!csvFile) return;
    setUploading(true);
    setResults(null);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (parsed) => {
        // Map CSV columns to expected format
        const mappings = parsed.data.map(row => ({
          vatNumber: row['VAT Number'] || row['vatNumber'] || row['vat_number'] || row['AFM'] || row['ΑΦΜ'] || '',
          erpCustomerCode: row['ERP Customer Code'] || row['erpCustomerCode'] || row['erp_customer_code'] || row['TRDR'] || ''
        })).filter(m => m.vatNumber && m.erpCustomerCode);

        if (mappings.length === 0) {
          setResults({ error: t('erp.bulkUpload.invalidFormat') });
          setUploading(false);
          return;
        }

        try {
          const res = await wholesalerAPI.bulkUploadErpCustomers(mappings);
          setResults(res.data.data || res.data);
          setCsvFile(null);
        } catch (err) {
          setResults({ error: err.response?.data?.message || 'Upload failed' });
        } finally {
          setUploading(false);
        }
      },
      error: () => {
        setResults({ error: t('erp.bulkUpload.invalidFormat') });
        setUploading(false);
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md mt-6 overflow-hidden">
      {/* Collapsible header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center min-w-0">
          <FileText className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
          <div className="min-w-0 text-left">
            <h3 className="text-sm sm:text-base font-semibold text-gray-600">{t('erp.bulkUpload.title')}</h3>
            <p className="text-gray-400 text-xs">{t('erp.bulkUpload.manualAlternative', 'Manual CSV alternative to automatic sync')}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t p-4 sm:p-6 pt-4">
          <p className="text-gray-500 text-xs sm:text-sm mb-4">{t('erp.bulkUpload.description')}</p>

          {/* CSV field reference */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
            <p className="font-semibold text-gray-700 mb-2">{t('erp.bulkUpload.csvFields', 'CSV Columns')}</p>
            <div className="space-y-1 text-xs text-gray-600">
              <p><span className="inline-block bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-bold mr-1.5">{t('common:required', 'REQUIRED')}</span><span className="font-mono">VAT Number</span> — {t('erp.bulkUpload.fieldVat', 'Business VAT/AFM number (must match an approved business)')}</p>
              <p><span className="inline-block bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-bold mr-1.5">{t('common:required', 'REQUIRED')}</span><span className="font-mono">ERP Customer Code</span> — {t('erp.bulkUpload.fieldTrdr', 'SoftOne TRDR code for this customer')}</p>
            </div>
          </div>

          {!uploading && !results && (
            <div className="space-y-4">
              {/* Step 1: Download template */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 bg-purple-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-purple-800">1. {t('erp.bulkUpload.downloadTemplate')}</p>
                  <p className="text-xs text-purple-600 mt-1">{t('erp.bulkUpload.templateDesc')}</p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium flex-shrink-0"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
              </div>

              {/* Step 2: Upload filled CSV */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">2. {t('erp.bulkUpload.uploadCsv')}</p>
                  {csvFile && (
                    <p className="text-xs text-green-600 mt-1 truncate">{csvFile.name}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                  <label className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer text-sm font-medium flex-shrink-0">
                    <Upload className="w-4 h-4" />
                    {t('erp.bulkUpload.selectFile')}
                    <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
                  </label>
                  {csvFile && (
                    <button
                      onClick={handleUpload}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex-shrink-0"
                    >
                      <Check className="w-4 h-4" />
                      {t('erp.bulkUpload.uploadCsv')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Uploading */}
          {uploading && (
            <div className="text-center py-8">
              <Loader className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{t('erp.bulkUpload.parsing')}</p>
            </div>
          )}

          {/* Results */}
          {results && !results.error && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">{t('erp.bulkUpload.results')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-purple-600">{results.stored || 0}</div>
                  <div className="text-xs text-gray-600">{t('erp.bulkUpload.stored', 'Saved to registry')}</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-green-600">{results.autoAssigned || 0}</div>
                  <div className="text-xs text-gray-600">{t('erp.bulkUpload.autoAssigned', 'Auto-assigned')}</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold text-blue-600">{results.totalInRegistry || 0}</div>
                  <div className="text-xs text-gray-600">{t('erp.bulkUpload.totalInRegistry', 'Total in registry')}</div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-3">
                {t('erp.bulkUpload.registryNote')}
              </p>

              <button
                onClick={() => { setResults(null); setCsvFile(null); }}
                className="w-full mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                {t('erp.bulkUpload.uploadAnother')}
              </button>
            </div>
          )}

          {/* Error */}
          {results?.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{results.error}</p>
              <button
                onClick={() => { setResults(null); }}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                {t('erp.bulkUpload.uploadAnother')}
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default CatalogRequests;